"use client";

import { MainContent } from "@/components";
import { HellmasterTokenBanner } from "@/components/HellmasterTokenBanner";
import { ReferralAcceptanceModal } from "@/components/QuestModals/ReferralAcceptanceModal";
import { CampaignProvider } from "@/components/QuestsV2/CampaignProvider";
import type { QuestViewData } from "@/components/QuestsV2/types";
import { TelegramConnectQuestBanner } from "@/components/TelegramConnectQuestBanner";
import { LoadingModal } from "@/components/core/LoadingModal";
import { MarketingConsentModal } from "@/components/core/MarketingConsentModal";
import { MultiGameDetailsLayout } from "@/components/layout/MultiGameDetailsLayout";
import { StandardGameDetailsLayout } from "@/components/layout/StandardGameDetailsLayout";
import { appConfig } from "@/constants";
import { usePassportProvider } from "@/context";
import {
  Action,
  UserJourney,
  useLoginAnalytics,
} from "@/context/AnalyticsProvider";
import { PointsProvider } from "@/context/PointsProvider";
import { usePreferences } from "@/context/PreferencesProvider";
import { QuestProvider } from "@/context/QuestProvider";
import { useWishlistHandler } from "@/hooks/useWishlistHandler";
import type { Quest } from "@/types";
import type { GameSpecifications } from "@/utils/cms/getGameSpecs";
import { fetcher } from "@/utils/fetch";
import {
  inFacebookBrowser,
  redirectGameToExternalBrowser,
} from "@/utils/loginredirect";
import { trackLegacyEvent } from "@analytics";
import { usePrevious } from "@biom3/react";
import { PremiumGameDetailsLayout } from "@components/layout/PremiumGameDetailsLayout";
import type { Content } from "@prismicio/client";
import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import { useSearchParams } from "next/navigation";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import useSWRImmutable from "swr/immutable";

const baseUrl = appConfig.IMMUTABLE_BASE_URI;

interface GameDetailsProps {
  game: Content.GameDocument;
  gameSpecs: GameSpecifications;
  page:
    | Content.GameDetailsPageDocument
    | Content.PremiumGameDetailsPageDocument
    | Content.MultiGameDetailsPageDocument;
  questsGroups:
    | Content.OnboardingQuestGroupDocument<string>[]
    | null
    | undefined;
  questViewData: QuestViewData;
}

export default function GameDetails({
  page,
  game,
  gameSpecs,
  questsGroups,
  questViewData,
}: GameDetailsProps) {
  const { facebookAdRedirectEnabled } = useFlags();
  const { data: preferencesData } = usePreferences();
  const previousPreferencesData = usePrevious(preferencesData);
  const { isLoggedIn, isLoggingIn } = usePassportProvider();
  const ldClient = useLDClient();
  const telegramB2cBotBannerEnabled: boolean =
    ldClient?.variation("b2c-telegram-bot-connect", false) ?? false;
  const [showMarketingConsent, setShowMarketingConsent] = useState(false);
  const [isClaimingOnboardingBonus, setIsClaimingOnboardingBonus] =
    useState(false);
  const searchParams = useSearchParams();
  const referralCode = searchParams?.get("referral") ?? null;
  const [showReferralAccept, setShowReferralAccept] = useState(false);
  const isFates = game?.uid === "fates-exp";

  // After fixing an issue where the login event was tracking multiple times (https://github.com/immutable/play/pull/1419)
  // it was discovered that we have funnels that rely on this as a starting point. As a result we must add this event here.
  // Do not remove without Data team's approval.
  const [trackedLogin, setTrackedLogin] = useState(false);
  const { track: trackLogin } = useLoginAnalytics();
  const pathname = usePathname();

  useEffect(() => {
    if (isLoggedIn && !trackedLogin) {
      trackLegacyEvent({
        screen: "Login",
        userJourney: "Profile",
        control: "Screen",
        controlType: "Effect",
        action: "Succeeded",
      });
      trackLogin({
        action: Action.SUCCEEDED,
        extras: {
          path: "/",
          page_path: pathname || "",
          screen: "Login",
          userJourney: UserJourney.PROFILE,
        },
      });
      setTrackedLogin(true);
    }
  }, [isLoggedIn, trackedLogin, pathname, trackLogin]);

  const gameUUID = (game?.data?.games_service_item?.id as string) || "";

  const { data = { quests: [] } } = useSWRImmutable<{ quests: Quest[] }>(
    gameUUID
      ? `${baseUrl}/v1/rewards/quests?external_id=${gameUUID}-wishlist`
      : null,
    fetcher,
    {
      errorRetryCount: 3,
      errorRetryInterval: 1000,
    },
  );

  const gemAmount =
    data.quests.length > 0
      ? data.quests.reduce((highest, current) =>
          current.id > highest.id ? current : highest,
        ).points
      : 0;

  const { gemsState, isWishlisted, addToWishlist, removeFromWishlist } =
    useWishlistHandler(gameUUID, gemAmount);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const followOnSuccess = urlParams.get("follow") === "true";
    if (followOnSuccess) {
      addToWishlist();
    }
  }, [addToWishlist]);

  // On fates-exp, never show the referral acceptance modal
  useEffect(() => {
    if (!referralCode) return;
    setShowReferralAccept(!isFates);
  }, [referralCode, isFates]);

  const [pendingConsentCheck, setPendingConsentCheck] = useState(false);
  const closeModal = useCallback(() => setShowMarketingConsent(false), []);
  const checkToOpenMarketingConsent = useCallback(() => {
    if (preferencesData.marketing_consent === "unsubscribed") {
      setShowMarketingConsent(true);
    }
  }, [preferencesData]);

  useEffect(() => {
    if (
      preferencesData !== previousPreferencesData &&
      pendingConsentCheck &&
      isLoggedIn
    ) {
      checkToOpenMarketingConsent();
    }
  }, [
    pendingConsentCheck,
    preferencesData,
    previousPreferencesData,
    checkToOpenMarketingConsent,
    isLoggedIn,
  ]);

  const onWishlistClick = useCallback(
    async (identifier: string, extras?: Record<string, unknown>) => {
      const inAdBrowser = inFacebookBrowser();
      const redirectEnabled = facebookAdRedirectEnabled === "VariantA";
      const eventExtras = inAdBrowser ? { ...extras, redirectEnabled } : extras;
      trackLegacyEvent({
        screen: "GameDetails",
        userJourney: "Games",
        action: "Pressed",
        control: isWishlisted ? "RemoveFromWishlist" : "AddToWishlist",
        controlType: "Button",
        extras: {
          userJourney: "Games",
          controlLabel: isWishlisted ? "RemoveFromWishlist" : "AddToWishlist",
          controlType: "Button",
          path: window.location.pathname,
          inAdBrowser,
          ...eventExtras,
        },
      });

      if (
        !redirectEnabled ||
        isWishlisted ||
        !redirectGameToExternalBrowser(true)
      ) {
        (isWishlisted ? removeFromWishlist : addToWishlist)();
        if (!isLoggedIn) {
          setPendingConsentCheck(true);
        } else if (!isWishlisted) {
          checkToOpenMarketingConsent();
        }
      }
    },
    [
      isWishlisted,
      addToWishlist,
      removeFromWishlist,
      checkToOpenMarketingConsent,
      isLoggedIn,
      facebookAdRedirectEnabled,
    ],
  );

  const wishlistProps = useMemo(() => {
    return {
      isWishlisted,
      gemsState,
      onWishlistClick,
      gemAmount,
    };
  }, [isWishlisted, gemsState, onWishlistClick, gemAmount]);

  const layout = useMemo(() => {
    const template = game?.data?.template_tier;
    switch (template) {
      case "premium":
        return (
          <PremiumGameDetailsLayout
            defaultBgImage={page?.data?.bg_image?.url}
            game={game}
            page={page as Content.PremiumGameDetailsPageDocument}
            gameSpecs={gameSpecs}
            wishlistProps={wishlistProps}
            questsGroups={questsGroups}
          />
        );
      case "multi":
        return (
          <MultiGameDetailsLayout
            defaultBgImage={page?.data?.bg_image?.url}
            game={game}
            page={page as Content.MultiGameDetailsPageDocument}
            gameSpecs={gameSpecs}
            wishlistProps={wishlistProps}
          />
        );
      case "standard":
        return (
          <StandardGameDetailsLayout
            defaultBgImage={page?.data?.bg_image?.url}
            game={game}
            page={page as Content.GameDetailsPageDocument}
            gameSpecs={gameSpecs}
            wishlistProps={wishlistProps}
          />
        );
    }
  }, [
    page,
    game,
    gameSpecs,
    game?.data?.template_tier,
    wishlistProps,
    questsGroups,
  ]);

  return (
    <MainContent
      banner={
        game.uid === "hellmaster" ? (
          <HellmasterTokenBanner />
        ) : telegramB2cBotBannerEnabled ? (
          <TelegramConnectQuestBanner />
        ) : undefined
      }
    >
      <MarketingConsentModal
        url={game?.data?.logo_square?.url ?? undefined}
        gameName={game?.data?.name ?? undefined}
        visible={showMarketingConsent}
        closeModal={closeModal}
      />
      <CampaignProvider
        imxGameId={(game.data.games_service_item?.id as string) ?? ""}
        questViewData={questViewData}
      >
        <QuestProvider>
          <PointsProvider>{layout}</PointsProvider>
        </QuestProvider>
      </CampaignProvider>
      <ReferralAcceptanceModal
        visible={showReferralAccept}
        referralCode={referralCode}
        questViewData={questViewData}
        gameData={game?.data}
        setShowReferralAccept={setShowReferralAccept}
      />
      <LoadingModal
        loading={isClaimingOnboardingBonus}
        text="Claiming your gems..."
      />
    </MainContent>
  );
}
